import { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import FuseUtils from '@fuse/utils/FuseUtils';
import withReducer from 'app/store/withReducer';
import { selectUser } from 'app/store/userSlice';
import { authPermissions } from 'app/auth';
import reducer from 'app/main/apps/e-commerce/store';
import {
  getOrders,
  getOrderStatuses,
  selectOrders,
  setCustomer,
  setSalesPerson,
  setSupplier,
  getCustomersList,
  resetOrders,
  getSuppliers,
  getContainerNumbersList,
  setResetFilters,
} from 'app/main/apps/e-commerce/store/ordersSlice';
import { getSalesPersons } from 'app/main/apps/client/store/customerSlice';
import AuraImport from 'app/main/apps/common/AuraImport';
import AuraExport from 'app/main/apps/common/AuraExport';
import { formatDateString, hasAnyPermissions, hasPermission, selectAmount } from 'app/main/apps/common/AuraFunctions';
import viewOrderspermissions from 'app/main/apps/common/PermissionConfig';
import getFilterOptions from 'app/main/apps/e-commerce/orders/filterOptions';
import { getListCapabilities } from 'app/main/apps/e-commerce/shared/listCapabilities';
import {
  formatVasTypesParam,
  parseVasTypesParam,
} from 'app/main/apps/e-commerce/shared/vasFilterChoices';
import Backdrop from '@mui/material/Backdrop';
import Box from '@mui/material/Box';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import CircularProgress from '@mui/material/CircularProgress';
import OrderDialog from 'app/main/apps/e-commerce/order/Dialogs/OrderDialog';
import ViewOrderDialog from 'app/main/dashboards/main/dialogs/ViewOrderDialog';
import ReallocationDialog from 'app/main/apps/e-commerce/order/Dialogs/ReallocationDialog';
import ChooseSupplier from 'app/main/apps/e-commerce/order/Dialogs/ChooseSupplier';
import CreateSupplierOrder from 'app/main/apps/e-commerce/order/Dialogs/SupplierShipping';
import ProductDialog from 'app/main/apps/e-commerce/product/dialogs/ProductDialog';
import ConfirmInvoiceDialog from 'app/main/apps/e-commerce/order/Dialogs/ConfirmInvoiceDialog';
import VasPreviewDialog from 'app/main/apps/e-commerce/order/Dialogs/VasPreviewDialog';
import StaffListView from './StaffListView';
import { applyListType, pushSearch, setFlag } from './StaffListFilters';
import {
  STAFF_LIST_COL_FILTER_PARAM_KEYS,
  staffListContainerUnsupportedSort,
  staffListSearchWithoutSort,
  staffListSortParamKey,
} from './StaffListColFilters';
import { getStaffListConfig } from './staffListConfig';
import {
  countStaffListFilters,
  mapStaffListRow,
  staffListContainerStatusCategory,
  staffListContainerTabFromUrl,
  staffListContainerTabSearch,
  staffListContainerUrlMissingTab,
  staffListContainerWithTab,
  STAFF_LIST_CONTAINER_TABS,
  staffListIsContainer,
  staffListLayoutUserId,
  staffListPartiesFromSearch,
  staffListSameParty,
  staffListScopeFromSearch,
  staffListShowsContainer,
  staffListShowsPaymentType,
  staffListShowsExport,
  staffListShowsMultipleImport,
  staffListShowsSingleImport,
  staffListTypeFromSearch,
  staffListDefaultHiddenColumns,
  staffListDefaultPageSize,
  staffListPageSizeOptions,
  staffListRemembersPageSize,
  staffListUrlMissingPageSize,
  staffListWithPageSize,
  readStaffListPageSize,
  writeStaffListPageSize,
} from './staffListUi';
import { STAFF_LIST_TEST_IDS } from '../staffListTestIds';
import {
  containerStatusOptions,
  rememberContainerTab,
  rememberedContainerTab,
} from 'app/main/apps/e-commerce/orders/ordersTab/containerShipmentsShared';
import './staffList.css';

function roleName(role) {
  return Array.isArray(role) ? role[0] : role;
}

function StaffListPage(props) {
  const listType = props.type || 'order';
  const isSupplierInvoice = props.for === 'supplier-invoices';
  const isContainerList = staffListIsContainer(listType);
  const config = getStaffListConfig(listType, { for: props.for });
  const { module } = props;
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const user = useSelector(selectUser);
  const role = roleName(user?.role);
  const isManage = user && FuseUtils.hasPermission(authPermissions.manage, user.role);
  const options = getFilterOptions(listType, role, isSupplierInvoice) || [];
  const [filterType, setSelectedFilterType] = useState(() => {
    const firstOptions = getFilterOptions(listType, role, isSupplierInvoice) || [];
    return staffListTypeFromSearch(window.location.search, firstOptions, listType) || firstOptions[0];
  });
  const [includeInvoice, setIncludeInvoice] = useState(
    () => staffListScopeFromSearch(window.location.search).invoiced
  );
  const [includeArchive, setIncludeArchive] = useState(
    () => staffListScopeFromSearch(window.location.search).archived
  );
  const [showOnlyVAS, setShowOnlyVAS] = useState(
    () => staffListScopeFromSearch(window.location.search).vas
  );
  const [actionableOnly, setActionableOnly] = useState(
    () => staffListScopeFromSearch(window.location.search).actionable
  );
  const [includeCancelled, setIncludeCancelled] = useState(
    () => staffListScopeFromSearch(window.location.search).cancelled
  );
  const [vasTypes, setVasTypes] = useState(() =>
    parseVasTypesParam(new URLSearchParams(window.location.search).get('vas_types')));
  const [openBackdrop, setOpenBackdrop] = useState(false);
  const listHeaders = useSelector(({ eCommerceApp }) => eCommerceApp?.orders?.orderListHeaders);
  const loading = useSelector(({ eCommerceApp }) => eCommerceApp?.orders?.loading);
  const entities = useSelector(selectOrders) || [];
  const customers = useSelector((state) => state.eCommerceApp.orders.customersListItems);
  const suppliers = useSelector((state) => state.eCommerceApp.orders.suppliersList);
  const salesPersons = useSelector((state) => state.eCommerceApp.orders.salesPersonListItems);
  const tabStatusNames = useSelector((state) => state.eCommerceApp.orders.orderStatusesNames) || [];
  // PGSS-5671: with Include Archive Container on, completed containers are listed too; offer their status.
  const statusNames = containerStatusOptions(tabStatusNames, listType, location.search);
  const containerNumbers = useSelector((state) => state.eCommerceApp.orders.containerNumbers) || [];
  const orderTypeNames = useSelector((state) => state.eCommerceApp.orders.orderTypeNames) || [];
  const paymentTypeNames = useSelector((state) => state.eCommerceApp.orders.paymentTypeNames) || [];
  const customer = useSelector((state) => state.eCommerceApp.orders.customer);
  const supplier = useSelector((state) => state.eCommerceApp.orders.supplier);
  const salesPerson = useSelector((state) => state.eCommerceApp.orders.salesPerson);
  const isReorderMode = filterType?.value === 'reorder';
  const isSupplierType = ['supplier', 'supplier_archive'].includes(filterType?.value);
  const can = getListCapabilities(user, listType, {
    canManage: isManage,
    isReorderMode,
    isSupplierInvoice,
  });
  // PGSS-5670: arriving without a tab (e.g. back from saving a container) reopens the remembered one.
  const containerViewType = isContainerList
    ? staffListContainerTabFromUrl(location.search, rememberedContainerTab())
    : null;
  const containerTabIndex = isContainerList
    ? Math.max(0, STAFF_LIST_CONTAINER_TABS.findIndex((tab) => tab.viewType === containerViewType))
    : 0;
  // OrdersToolBar / row actions refetch from the URL alone, so the tab has to be in it.
  const containerUrlMissingTab = isContainerList && staffListContainerUrlMissingTab(location.search);
  // PGSS-5655: an old bookmark can carry a sort the container API cannot run (sort=weight → 500).
  const containerUnsupportedSort = isContainerList && staffListContainerUnsupportedSort(location.search);
  // PGSS-5753: RFQ / Quote / Order always carry `limit` in the URL; the effect below writes the
  // remembered size in on arrival, and the list waits for it.
  const urlMissingPageSize = staffListUrlMissingPageSize(location.search, listType);
  const pageSize = Number(
    new URLSearchParams(location.search).get('limit')
      || (staffListRemembersPageSize(listType) ? null : listHeaders?.['app-per-page-limit'])
      || staffListDefaultPageSize(listType)
  );
  const total = Number(listHeaders?.['app-pagination-total-records'] || 0);
  const page = Number(new URLSearchParams(location.search).get('pageNo') || 0);
  const pageCount = pageSize ? Math.max(1, Math.ceil(total / pageSize)) : 1;

  useEffect(() => {
    return () => {
      dispatch(resetOrders());
    };
  }, [dispatch]);

  useEffect(() => {
    if (!isContainerList) {
      dispatch(getCustomersList({ lt: 'n' }));
      dispatch(getSuppliers());
      dispatch(getSalesPersons({ isFilter: true }));
    }
    // PGSS-5556: the Container column and its filter render wherever staffListShowsContainer says
    // so — orders AND invoices — but this only loaded the options for orders. On the invoice list
    // the picker had nothing to choose from, so no container could be applied and the list stayed
    // unfiltered. resetOrders() clears containerNumbers on unmount, so arriving from Orders does
    // not leave a populated list behind either.
    if (staffListShowsContainer(listType)) {
      dispatch(getContainerNumbersList());
    }
    dispatch(getOrderStatuses({ params: { c: 'order_type' } }));
  }, [dispatch, listType, isContainerList]);

  // PGSS-5634: customer invoices search by the customer's payment type. Leaving this page
  // clears paymentTypeNames, so the choices have to be loaded here, not left over from elsewhere.
  // Kept out of the effect above so switching customer/supplier invoices does not refetch those lists.
  useEffect(() => {
    if (staffListShowsPaymentType(listType, isSupplierInvoice)) {
      dispatch(getOrderStatuses({ params: { c: 'payment_type' } }));
    }
  }, [dispatch, listType, isSupplierInvoice]);

  useEffect(() => {
    const params = {
      c: isContainerList ? staffListContainerStatusCategory(containerViewType) : config.statusCategory,
    };
    if (filterType?.value === 'reorder') {
      params.type = 'reorder';
    }
    dispatch(getOrderStatuses({ params }));
  }, [dispatch, config.statusCategory, filterType?.value, isContainerList, containerViewType]);

  useEffect(() => {
    if (isContainerList && !containerUrlMissingTab) rememberContainerTab(containerViewType);
  }, [isContainerList, containerUrlMissingTab, containerViewType]);

  useEffect(() => {
    if (!containerUrlMissingTab) return;
    navigate(
      { pathname: location.pathname, search: staffListContainerWithTab(location.search, containerViewType) },
      { replace: true }
    );
  }, [containerUrlMissingTab, containerViewType, location.pathname, location.search, navigate]);

  useEffect(() => {
    if (!urlMissingPageSize) return;
    navigate(
      { pathname: location.pathname, search: staffListWithPageSize(location.search, readStaffListPageSize(listType)) },
      { replace: true }
    );
  }, [urlMissingPageSize, listType, location.pathname, location.search, navigate]);

  useEffect(() => {
    if (!containerUnsupportedSort) return;
    navigate({ pathname: location.pathname, search: staffListSearchWithoutSort(location.search) }, { replace: true });
  }, [containerUnsupportedSort, location.pathname, location.search, navigate]);

  useEffect(() => {
    const nextOptions = getFilterOptions(listType, role, isSupplierInvoice) || [];
    const nextType = staffListTypeFromSearch(location.search, nextOptions, listType);
    if (nextType) {
      setSelectedFilterType((prev) => (prev?.value === nextType?.value ? prev : nextType));
    }
    const scope = staffListScopeFromSearch(location.search);
    setIncludeInvoice(scope.invoiced);
    setIncludeArchive(scope.archived);
    setShowOnlyVAS(scope.vas);
    setActionableOnly(scope.actionable);
    setIncludeCancelled(scope.cancelled);
    setVasTypes((prev) => {
      const next = parseVasTypesParam(new URLSearchParams(location.search).get('vas_types'));
      return prev.join(',') === next.join(',') ? prev : next;
    });
  }, [location.search, listType, role, isSupplierInvoice]);

  useEffect(() => {
    const nextOptions = getFilterOptions(listType, role, isSupplierInvoice) || [];
    const type = staffListTypeFromSearch(location.search, nextOptions, listType) || filterType;
    const next = staffListPartiesFromSearch(location.search, {
      customers,
      suppliers,
      salesPersons,
      typeValue: type?.value,
    });
    if (!next.customerPending && !staffListSameParty(customer, next.customer)) {
      dispatch(setCustomer(next.customer));
    }
    if (!next.supplierPending && !staffListSameParty(supplier, next.supplier)) {
      dispatch(setSupplier(next.supplier));
    }
    if (!next.salesPending && !staffListSameParty(salesPerson, next.salesPerson)) {
      dispatch(setSalesPerson(next.salesPerson));
    }
  }, [
    location.search,
    listType,
    role,
    isSupplierInvoice,
    customers,
    suppliers,
    salesPersons,
    customer,
    supplier,
    salesPerson,
    filterType,
    dispatch,
  ]);

  // getOrders merges getParams() from the URL over these, so cid/sid/scope are
  // already correct on the first request. Do not wait on party lists (that only
  // delays it) and do not depend on Redux people (hydrate would refetch the same URL).
  //
  // PGSS-5449: lifted out of the effect so Refresh can repeat exactly this request.
  // PGSS-5546: isSupplierInvoice / include_cancelled are part of the request identity.
  const fetchOrders = useCallback(() => {
    const isContainer = staffListIsContainer(listType);
    if (staffListUrlMissingPageSize(location.search, listType)) return; // PGSS-5753
    // Wait for the URL to carry the tab so the list is not requested twice on arrival.
    if (isContainer && staffListContainerUrlMissingTab(location.search)) return;
    // ...and not with a sort it cannot run; the effect above drops it and the URL change refetches.
    if (isContainer && staffListContainerUnsupportedSort(location.search)) return;
    const scope = staffListScopeFromSearch(location.search);
    dispatch(getOrders({
      params: {
        stage: listType,
        include_invoice_status: scope.invoiced,
        include_archive: scope.archived,
        vas_order: scope.vas,
        actionableOnly: scope.actionable,
        ...(isSupplierInvoice ? { is_supplier: true } : {}),
        ...(scope.cancelled ? { include_cancelled: true } : {}),
        ...(isContainer ? { viewType: staffListContainerTabFromUrl(location.search) } : {}),
      },
    }));
  }, [dispatch, listType, location.search, isSupplierInvoice]);

  // The effect only refires when the URL changes, which is why there was no way to re-request the
  // same URL -- the gap this ticket reports.
  useEffect(() => {
    fetchOrders();
  }, [fetchOrders]);

  const viewPermissions = listType === 'order' ? viewOrderspermissions : null;
  const canView =
    hasPermission(user, 'view', module, isManage) ||
    (viewPermissions && hasAnyPermissions(user, viewPermissions));
  const canCreate = Boolean(config.showCreate) && isManage && hasPermission(user, 'create', module);
  const canImport = isManage && hasPermission(user, 'import', module);
  const canImportMultiple = canImport && staffListShowsMultipleImport(listType);
  const canImportSingle = canImport && staffListShowsSingleImport(listType);
  const canExport = isManage && hasPermission(user, 'export', module) && staffListShowsExport(listType);

  const rows = useMemo(
    () => entities.map((order) => mapStaffListRow(order, formatDateString, selectAmount, listType)).filter(Boolean),
    [entities, listType]
  );

  const people = isSupplierType ? supplier : customer;
  const peopleOptions = isSupplierType
    ? [{ name: 'All', id: 'all' }, ...(suppliers || [])]
    : (customers || []);
  const filterCount = countStaffListFilters({
    filterType,
    defaultType: options[0]?.value,
    customer: people,
    salesPerson,
    includeInvoice,
    includeArchive,
    showOnlyVAS,
    vasTypes,
    actionableOnly,
    includeCancelled,
  });

  const resetFilters = () => {
    dispatch(setCustomer(null));
    dispatch(setSalesPerson(null));
    dispatch(setSupplier(null));
    setSelectedFilterType(staffListTypeFromSearch('', options, listType) || options[0]);
    setIncludeInvoice(false);
    setIncludeArchive(false);
    setShowOnlyVAS(false);
    setActionableOnly(false);
    setIncludeCancelled(false);
    setVasTypes([]);
    navigate(
      isContainerList
        ? { pathname: location.pathname, search: staffListContainerTabSearch(containerViewType) }
        : location.pathname,
      { replace: true }
    );
    dispatch(setResetFilters(true));
  };

  const searchParams = useMemo(
    () => Object.fromEntries(new URLSearchParams(location.search)),
    [location.search]
  );
  const [sortKey, sortDir] = String(searchParams.sort || '').split(',');

  const handleContainerTabChange = (_event, nextIndex) => {
    const next = STAFF_LIST_CONTAINER_TABS[nextIndex];
    if (!next) return;
    navigate(
      { pathname: location.pathname, search: staffListContainerTabSearch(next.viewType) },
      { replace: true }
    );
  };

  const handlePeopleChange = (next) => {
    const sp = new URLSearchParams(window.location.search);
    sp.delete('sid');
    sp.delete('cid');
    sp.delete('pageNo');
    if (isSupplierType) {
      dispatch(setSupplier(next));
      dispatch(setCustomer(null));
      sp.set('sid', next?.id || 'all');
    } else {
      dispatch(setCustomer(next));
      dispatch(setSupplier(null));
      if (next?.id) sp.set('cid', next.id);
    }
    pushSearch(sp, navigate);
  };

  const handleSalesChange = (next) => {
    const sp = new URLSearchParams(window.location.search);
    sp.delete('sales_person');
    sp.delete('pageNo');
    dispatch(setSalesPerson(next));
    if (next?.id) sp.set('sales_person', next.id);
    pushSearch(sp, navigate);
  };

  const handleReorderDate = (next) => {
    const sp = new URLSearchParams(window.location.search);
    if (next) sp.set('date', next);
    else sp.delete('date');
    sp.delete('pageNo');
    pushSearch(sp, navigate);
  };

  const handlePageSize = (limit) => {
    const sp = new URLSearchParams(window.location.search);
    if (staffListRemembersPageSize(listType)) {
      writeStaffListPageSize(listType, Number(limit));
      sp.set('limit', limit);
    } else if (Number(limit) === 10) sp.delete('limit');
    else sp.set('limit', limit);
    sp.delete('pageNo');
    pushSearch(sp, navigate);
  };

  const writeVasTypes = (types) => {
    const sp = new URLSearchParams(window.location.search);
    const csv = formatVasTypesParam(types);
    setVasTypes(types);
    if (csv) sp.set('vas_types', csv);
    else sp.delete('vas_types');
    sp.delete('pageNo');
    pushSearch(sp, navigate);
  };

  const handlePageChange = (nextPage) => {
    const sp = new URLSearchParams(window.location.search);
    if (nextPage > 0) sp.set('pageNo', nextPage);
    else sp.delete('pageNo');
    pushSearch(sp, navigate);
  };

  const handleSort = (colKey) => {
    const paramKey = staffListSortParamKey(colKey, { listType, isSupplierType });
    if (!paramKey) return;
    const sp = new URLSearchParams(window.location.search);
    const [currentKey, currentDir] = (sp.get('sort') || '').split(',');
    const dir = currentKey === paramKey && currentDir === 'DESC' ? 'ASC' : 'DESC';
    sp.set('sort', `${paramKey},${dir}`);
    pushSearch(sp, navigate);
  };

  /** One param, or several at once as `{ key: value }` (one history entry). Empty values are removed. */
  const updateColParam = (key, value) => {
    const sp = new URLSearchParams(window.location.search);
    const updates = typeof key === 'object' && key !== null ? key : { [key]: value };
    Object.entries(updates).forEach(([k, v]) => {
      const empty = v === '' || v == null || (Array.isArray(v) && v.length === 0);
      if (empty) {
        sp.delete(k);
      } else {
        sp.set(k, Array.isArray(v) ? v.join(',') : v);
      }
    });
    sp.delete('pageNo');
    pushSearch(sp, navigate);
  };

  const clearColFilters = () => {
    const sp = new URLSearchParams(window.location.search);
    STAFF_LIST_COL_FILTER_PARAM_KEYS.forEach((key) => sp.delete(key));
    sp.delete('pageNo');
    pushSearch(sp, navigate);
  };

  if (!canView) {
    return null;
  }

  return (
    <>
      {isContainerList && (
        <Box
          sx={{
            position: 'sticky',
            top: 0,
            zIndex: (theme) => theme.zIndex.appBar,
            borderBottom: 1,
            borderColor: 'divider',
            backgroundColor: (theme) => theme.palette.background.default,
            px: 1.5,
          }}
        >
          <Tabs
            value={containerTabIndex}
            onChange={handleContainerTabChange}
            aria-label="container shipment tabs"
            data-testid={STAFF_LIST_TEST_IDS.containerTabs}
            variant="scrollable"
            scrollButtons="auto"
            allowScrollButtonsMobile
          >
            {STAFF_LIST_CONTAINER_TABS.map((tab) => (
              <Tab
                key={tab.viewType}
                label={tab.label}
                data-testid={STAFF_LIST_TEST_IDS.containerTab(tab.viewType)}
                className="text-[#4f46e5]"
              />
            ))}
          </Tabs>
        </Box>
      )}
      <StaffListView
        orders={rows}
        title={config.title}
        subtitle={isContainerList
          ? STAFF_LIST_CONTAINER_TABS[containerTabIndex]?.label
          : (filterType?.name || config.title)}
        noun={config.noun}
        newLabel={config.newLabel}
        listType={listType}
        role={role}
        isManage={isManage}
        isSupplierInvoice={isSupplierInvoice}
        typeOptions={options}
        typeValue={filterType?.value}
        onTypeChange={(value) => {
          const next = options.find((option) => option.value === value);
          if (next) applyListType(next, setSelectedFilterType, dispatch, navigate);
        }}
        filterType={filterType}
        isSupplierType={isSupplierType}
        isReorderMode={isReorderMode}
        reorderDate={searchParams.date}
        onReorderDateChange={handleReorderDate}
        peopleLabel={isSupplierType ? 'Supplier' : 'Customer'}
        peopleOptions={peopleOptions}
        people={people}
        onPeopleChange={handlePeopleChange}
        showPeople={!isReorderMode && !isContainerList}
        salesOptions={salesPersons || []}
        salesPerson={salesPerson}
        onSalesChange={handleSalesChange}
        showSales={!isReorderMode && !isSupplierType && !isContainerList}
        scope={{
          invoiced: includeInvoice,
          archived: includeArchive,
          vas: showOnlyVAS,
          actionable: actionableOnly,
          cancelled: includeCancelled,
        }}
        showScope={{
          invoiced: can.filterIncludeInvoice,
          archived: can.filterIncludeArchive && (!isContainerList || Boolean(isManage)),
          vas: can.filterShowOnlyVAS,
          actionable: can.filterActionableOnly,
          cancelled: can.filterCancelledInvoices,
        }}
        onToggleScope={(key) => {
          if (key === 'invoiced') setFlag('include_invoice_status', !includeInvoice, setIncludeInvoice, navigate);
          if (key === 'archived') setFlag('include_archive', !includeArchive, setIncludeArchive, navigate);
          if (key === 'vas') {
            const next = !showOnlyVAS;
            setFlag('vas_order', next, setShowOnlyVAS, navigate);
            if (!next) writeVasTypes([]);
          }
          if (key === 'actionable') setFlag('actionableOnly', !actionableOnly, setActionableOnly, navigate);
          if (key === 'cancelled') setFlag('include_cancelled', !includeCancelled, setIncludeCancelled, navigate);
        }}
        vasTypes={vasTypes}
        onVasTypesChange={writeVasTypes}
        filterCount={filterCount}
        onResetFilters={resetFilters}
        onClearColFilters={clearColFilters}
        layoutUserId={staffListLayoutUserId(user)}
        shownCount={rows.length}
        totalCount={total}
        page={page}
        pageSize={pageSize}
        pageSizeOptions={staffListPageSizeOptions(listType)}
        pageCount={pageCount}
        onPageChange={handlePageChange}
        onPageSizeChange={handlePageSize}
        sortKey={sortKey}
        sortDir={sortDir}
        onSort={handleSort}
        colParams={searchParams}
        updateColParam={updateColParam}
        statusNames={statusNames}
        containerNumbers={containerNumbers}
        orderTypeNames={orderTypeNames}
        paymentTypeNames={paymentTypeNames}
        showCreate={canCreate}
        hiddenColumns={staffListDefaultHiddenColumns(listType)}
        loading={loading}
        onRefresh={fetchOrders}
        onNewOrder={() => config.newLink && navigate(config.newLink)}
        headerActions={[
          isContainerList && isManage && {
            key: 'shipment-tracking',
            label: 'Shipment Tracking',
            icon: 'location',
            testId: STAFF_LIST_TEST_IDS.shipmentTracking,
            onClick: () => navigate('/container-tracking'),
          },
          canImportMultiple && {
            key: 'import-multiple',
            group: 'import',
            section: 'Import',
            label: 'Import multiple',
            icon: 'upload',
            inputId: `button-filestaff-list-import-multiple-${listType}`,
            node: (
              <AuraImport
                id={`staff-list-import-multiple-${listType}`}
                name={config.importName}
                title={config.importTitle}
                display="open_file"
                handleBackdropToggle={setOpenBackdrop}
              />
            ),
          },
          canImportSingle && {
            key: 'import-single',
            group: 'import',
            section: 'Import',
            label: 'Import single',
            icon: 'upload',
            inputId: `button-filestaff-list-import-single-${listType}`,
            node: (
              <AuraImport
                id={`staff-list-import-single-${listType}`}
                name={config.importName}
                title={`Import Single ${config.title.replace(/s$/, '')}`}
                display="open_file"
                params={{ single: true }}
                handleBackdropToggle={setOpenBackdrop}
              />
            ),
          },
          canImportMultiple && {
            key: 'import-archive',
            group: 'import',
            section: 'Import',
            label: 'Import archive',
            icon: 'archive',
            inputId: `button-filestaff-list-import-archive-${listType}`,
            node: (
              <AuraImport
                id={`staff-list-import-archive-${listType}`}
                name={config.importName}
                title={`Import Archive ${config.title}`}
                display="open_file"
                icon="archive"
                params={{ archive: 'yes' }}
                handleBackdropToggle={setOpenBackdrop}
              />
            ),
          },
          canImportMultiple && {
            key: 'view-imports',
            group: 'import',
            section: 'History',
            label: 'View imports',
            icon: 'lines',
            to: '/orders/imports',
          },
          canImportSingle && {
            key: 'view-single-imports',
            group: 'import',
            section: 'History',
            label: 'View single imports',
            icon: 'lines',
            to: '/orders/single-imports',
          },
          canImportMultiple && listType === 'order' && {
            key: 'sample-single',
            group: 'import',
            section: 'Samples',
            label: 'Sample import',
            icon: 'excel',
            href: '/sampleFiles/order/single_order_import_excel_format.xlsx',
            download: true,
            title: 'Download single order import excel format',
          },
          canImportMultiple && listType === 'order' && {
            key: 'sample-price',
            group: 'import',
            section: 'Samples',
            label: 'Sample with price',
            icon: 'excel',
            href: '/sampleFiles/order/single_order_import_with_supplier_price_excel_format.xlsx',
            download: true,
            title: 'Download single order with supplier price import excel format',
          },
          canExport && {
            key: 'export',
            label: 'Export',
            icon: 'download',
            node: (
              <AuraExport
                title={config.exportTitle}
                display="list"
                params={{
                  stage: listType,
                  parentFilterType: isSupplierType ? 'supplier' : 'customer',
                }}
                value={listType}
                stage={listType}
              />
            ),
          },
        ].filter(Boolean)}
      />
      <Backdrop open={Boolean(openBackdrop)} style={{ zIndex: 1301, color: '#fff' }}>
        <CircularProgress color="inherit" />
      </Backdrop>
      <OrderDialog />
      <ViewOrderDialog isDashboard={false} />
      <ReallocationDialog />
      <ChooseSupplier />
      <CreateSupplierOrder />
      <ProductDialog />
      <ConfirmInvoiceDialog />
      <VasPreviewDialog />
    </>
  );
}

export default withReducer('eCommerceApp', reducer)(StaffListPage);
